import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import { getMarket } from '@atc/bonnet-reference';
import { formatCurrency } from '@atc/string-fns';

import {
    AccordionPanel,
    Alert,
    Button,
    LoadingSpinner,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxEmailUsClick, psxVDPEstimateDeliveryCostZipSubmit } from 'reaxl-analytics-handlers';
import {
    Form,
    StandardZipInput,
} from 'reaxl-forms';

import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import PsxHomeDeliveryFetcher from '@/fetchers/vdp/PsxHomeDeliveryFetcher';

import SubSectionHeader from './SubSectionHeader';

function DeliveryEstimate({ price, distance }) {
    return (
        <div className="margin-top-2">
            Estimated Delivery Cost:&nbsp;
            <Text
                size={400}
                weight="bold"
            >
                {`${price}`}
            </Text>
            <Text weight="bold">
                {` (Est. Distance: ${distance} mi.)`}
            </Text>
        </div>
    );
}

function PsxHandleDelivery() {

    const userZip = useSelector(userDuck?.selectors?.getZip);
    const listing = useSelector(vdpResultsDuck?.selectors?.getVdpActiveInventory);
    const { vin, id: listingId } = listing;

    const [deliveryEstimate, setDeliveryEstimate] = useState(null);
    const [showFetchErrorMessage, setShowFetchErrorMessage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onZipSubmit = async (event) => {
        setIsLoading(true);
        setShowFetchErrorMessage(false);
        const zip = event.inputZip;
        await getMarket(zip) // checking for valid ZIP code
            .then((async (validation) => {
                if (validation?.success) {
                    await PsxHomeDeliveryFetcher({ zip, vin })
                        .then((response) => {
                            if (!response || response?.results === false) {
                                setShowFetchErrorMessage(true);
                            } else {
                                setDeliveryEstimate(response);
                            }
                        })
                        .catch(() => setShowFetchErrorMessage(true));
                } else {
                    setShowFetchErrorMessage(true);
                }
            }))
            .catch(() => setShowFetchErrorMessage(true))
            .finally(() => setIsLoading(false));
    };

    const handleZipSubmit = (event) => {
        sendClick(psxVDPEstimateDeliveryCostZipSubmit, event, { listingId });
    };

    const handleEmailUsClick = (event) => {
        sendClick(psxEmailUsClick, event, { listingId });
    };

    return (
        <AccordionPanel
            title="How We Handle Delivery"
            key="delivery"
            eventKey="delivery"
        >
            <SubSectionHeader
                heading="Get it Delivered (PAID)"
                description="Provide your delivery address and we&rsquo;ll ship the car to you anywhere in the contiguous US. We&rsquo;ll walk you through the shipping process. And don&rsquo;t worry, we&rsquo;re bonded and insured."
                footer="Enter your zip code to estimate your delivery cost:"
            />
            <div className="row margin-bottom-3">
                <div className="col-xs-8 margin-left-2 padding-left-0">
                    <Form onSubmit={onZipSubmit}>
                        <div className="display-flex">
                            <StandardZipInput
                                label="Zip Code"
                                layout="inside"
                                name="inputZip"
                                id="inputZip"
                                value={userZip}
                                required
                            />
                            <div className="margin-horizontal-2 margin-top-md-0">
                                <Button
                                    bsStyle="secondary"
                                    type="submit"
                                    disabled={isLoading}
                                    onClick={handleZipSubmit}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
            {isLoading && <LoadingSpinner className="margin-top-0" />}
            {deliveryEstimate?.price && deliveryEstimate?.miles && !showFetchErrorMessage && !isLoading && (
                <DeliveryEstimate
                    price={formatCurrency(deliveryEstimate.price)}
                    distance={deliveryEstimate.miles}
                />
            )}
            {showFetchErrorMessage && (
                <Alert
                    bsStyle="danger"
                    className="margin-vertical-3 text-center"
                >
                    <Text
                        className="text-bold"
                        componentClass="div"
                    >
                        Sorry, We could not get a delivery estimate for this vehicle for the given pickup and delivery location.
                    </Text>
                    <div>
                        If you have questions or need help,&nbsp;
                        <a
                            onClick={handleEmailUsClick}
                            href="https://www.autotrader.com/email-us?contactEmail=atc_feedback"
                        >
                            email us.
                        </a>
                    </div>
                </Alert>
            )}
            <p className="margin-top-3">
                Estimated delivery cost based on vehicle make and distance between pickup and delivery locations. Amounts are estimates only and not a guarantee of final cost.
            </p>
        </AccordionPanel>
    );
}
export default React.memo(PsxHandleDelivery);
